import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, Sparkles, Users, CalendarDays, History, MessagesSquare, Ticket, Heart, Star } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const intervalLabel = (i) => (i === 'month' ? '/mo' : i === 'year' ? '/yr' : '');

// Pick a contextual icon for a free-tier benefit based on keywords.
const benefitIcon = (text) => {
  const t = (text || '').toLowerCase();
  if (t.includes('community') || t.includes('sangha') || t.includes('member')) return Users;
  if (t.includes('feed') || t.includes('discussion') || t.includes('chat')) return MessagesSquare;
  if (t.includes('register') || t.includes('ticket') || t.includes('book')) return Ticket;
  if (t.includes('history') || t.includes('past')) return History;
  if (t.includes('event') || t.includes('retreat') || t.includes('program')) return CalendarDays;
  if (t.includes('meditation') || t.includes('heart') || t.includes('wellbeing')) return Heart;
  return Star;
};

const MembershipPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [basic, setBasic] = useState({ name: 'Seekers Community Membership', tagline: 'Free forever', benefits: ['Community feed & events', 'Register for events', 'Event history'] });
  const [loading, setLoading] = useState(true);
  const [checkoutId, setCheckoutId] = useState(null);

  useEffect(() => {
    api.get('/memberships/plans')
      .then((res) => setPlans(res.data || []))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
    api.get('/memberships/basic').then((res) => res.data && setBasic(res.data)).catch(() => {});
  }, []);

  const subscribe = async (plan) => {
    if (!user) {
      navigate(`/signup?plan=${plan.id}`);
      return;
    }
    setCheckoutId(plan.id);
    try {
      const res = await api.post('/payments/checkout', {
        plan_id: plan.id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast({ title: 'Checkout failed', description: err?.response?.data?.detail || 'Please try again.' });
      setCheckoutId(null);
    }
  };

  return (
    <div className="App">
      <Header />
      <main className="bg-cream">
        <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 text-center">
          <div className="eyebrow mb-3">Membership</div>
          <h1 className="font-serif-display text-4xl md:text-5xl text-burgundy">
            Choose Your <span className="font-italic-display text-rose-italic">Path</span>
          </h1>
          <p className="mt-3 text-mahogany/80 max-w-2xl mx-auto">
            Deepen your journey with benefits that grow with your commitment — from community access to residential living.
          </p>

          {loading ? (
            <div className="flex justify-center mt-16"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div>
          ) : (
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
              {/* Free Basic card */}
              <PlanCard
                free
                plan={{ name: basic.name, tagline: basic.tagline, price: 0, benefits: basic.benefits }}
                current={!!user && (!user?.membership?.tier || user?.membership?.tier === 'basic')}
                onJoin={!user ? () => navigate('/signup?plan=basic') : undefined}
              />
              {plans.map((p) => (
                <PlanCard
                  key={p.id}
                  plan={p}
                  current={user?.membership?.tier === p.tier_key}
                  loading={checkoutId === p.id}
                  onSubscribe={() => subscribe(p)}
                />
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

const PlanCard = ({ plan, free, current, loading, onSubscribe, onJoin }) => (
  <div className={`relative p-7 rounded-3xl border flex flex-col ${plan.highlight ? 'border-mahogany bg-mahogany/5 shadow-lg' : 'border-rose/50 bg-white/70'}`}>
    {plan.highlight && (
      <span className="absolute -top-3 left-7 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-3 py-1 rounded-full bg-mahogany text-cream">
        <Sparkles className="w-3 h-3" /> Most Popular
      </span>
    )}
    <div className="font-serif-display text-2xl text-burgundy">{plan.name}</div>
    {plan.tagline && <div className="text-sm text-mahogany/70 mt-1">{plan.tagline}</div>}
    <div className="mt-4 flex items-end gap-1">
      <span className="font-serif-display text-4xl text-burgundy">{plan.price > 0 ? `$${plan.price}` : 'Gift for Seeker'}</span>
      {plan.price > 0 && <span className="text-mahogany/60 mb-1">{intervalLabel(plan.interval)}</span>}
    </div>
    <ul className="mt-5 space-y-2.5 flex-1">
      {(plan.benefits || []).map((b) => {
        const Ico = free ? benefitIcon(b) : Check;
        return (
          <li key={b} className="flex items-start gap-2.5 text-sm text-burgundy/85">
            {free ? (
              <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-rose-soft flex items-center justify-center">
                <Ico className="w-3.5 h-3.5 text-mahogany" />
              </span>
            ) : (
              <Check className="w-4 h-4 text-mahogany mt-0.5 shrink-0" />
            )}
            <span className="mt-0.5">{b}</span>
          </li>
        );
      })}
    </ul>
    <div className="mt-6">
      {current ? (
        <div className="pill-btn-outline justify-center w-full opacity-70 cursor-default">Current Plan</div>
      ) : free ? (
        onJoin ? (
          <button onClick={onJoin} className="pill-btn justify-center w-full" data-testid="join-as-seeker-btn">Join as Seeker</button>
        ) : (
          <div className="text-center text-sm text-mahogany/60 py-3">Included by default</div>
        )
      ) : (
        <button onClick={onSubscribe} disabled={loading} className="pill-btn justify-center w-full">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Choose {plan.name}
        </button>
      )}
    </div>
  </div>
);

export default MembershipPage;
